// Flatten a document to a PNG at full document resolution. Runs in the
// browser only; layout math (wrap/align/z) is pure and tested in
// @/lib/flyer-compose — this file just drives Canvas2D with it.
import type { FlyerDocElement } from '@/types/flyerDoc';
import { alignedX, styleToFont, wrapLines, zSorted } from '@/lib/flyer-compose';

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`image_load_failed: ${url}`));
    img.src = url;
  });

export async function exportDocPng(doc: {
  width: number; height: number; doc_json: FlyerDocElement[]; background_url: string | null;
}): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = doc.width;
  canvas.height = doc.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas_2d_unavailable');

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, doc.width, doc.height);
  if (doc.background_url) ctx.drawImage(await loadImage(doc.background_url), 0, 0, doc.width, doc.height);

  for (const el of zSorted(doc.doc_json)) {
    const x = el.x_percent * doc.width;
    const y = el.y_percent * doc.height;
    const w = el.width_percent * doc.width;
    const h = el.height_percent * doc.height;
    if (el.kind === 'image' && el.src_url) {
      ctx.drawImage(await loadImage(el.src_url), x, y, w, h);
    } else if (el.kind === 'text') {
      ctx.font = styleToFont(el.style);
      ctx.fillStyle = el.style?.color ?? '#000000';
      ctx.textBaseline = 'top';
      const { x: ax, textAlign } = alignedX(el.style?.alignment, x, w);
      ctx.textAlign = textAlign;
      const size = el.style?.fontSize ?? 48;
      const lines = wrapLines((s) => ctx.measureText(s).width, el.text ?? '', w);
      lines.forEach((line, i) => {
        const ly = y + i * size * 1.2;
        if (ly + size <= y + h + size * 0.5) ctx.fillText(line, ax, ly);
      });
    }
  }
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('to_blob_failed'))), 'image/png'));
}
